/* eslint-disable react-hooks/exhaustive-deps */
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import {
  updateObservationTest,
  getObservationTestTypes,
  orderTestImages,
} from "../../../../../services/ObservationService";
import { uploadToS3 } from "../../../../../services/s3";
import CustomInput from "../../../../components/Custom/CustomInput";
import ImageBox from "../../../../components/ImageBox/ImageBox";
import SearchDropdown from "../../../../components/SearchDropdown";
import { getErrorMsg, showToast } from "../../../../shared/helpers";
import { fileSizeLimits, folders } from "../../../../shared/validations";
// Add dnd-kit imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CustomTooltip from "main/components/Tooltip/Tooltip";

export default function EditTestModal(props) {
  const { test, show, onHide, insp_id, setRefreshTestList } = props;

  const [testImagesPreview, setTestImagesPreview] = useState([]);
  const [testImages, setTestImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingImageText] = useState("Uploading images...");
  const [disableSampleSelect, setDisableSampleSelect] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState(null);
  const [selectedSampleType, setSelectedSampleType] = useState(null);
  const [disableSave, setDisableSave] = useState(false);
  const [markAsComplete, setMarkAsComplete] = useState(false);
  const [sampleTypesData, setSampleTypesData] = useState([]);
  const [sampleInfo, setSampleInfo] = useState([]);
  const [sampleInfoLabel, setSampleInfoLabel] = useState([]);
  const [fileTypeError, setFileTypeError] = useState("");
  // Removed unused activeId state
  const [isDelete, setIsDelete] = useState([]);
  const [hasDragged, setHasDragged] = useState(false);

  const imageInput = useRef(null);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // dnd-kit drag end handler
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = testImagesPreview.findIndex((url) => url === active.id);
      const newIndex = testImagesPreview.findIndex((url) => url === over.id);

      // New order of previews
      const newPreviewOrder = arrayMove(testImagesPreview, oldIndex, newIndex);

      // Update preview state
      setTestImagesPreview(newPreviewOrder);

      // Update imgIndex of files according to new preview order
      setTestImages((files) => {
        newPreviewOrder.forEach((url, index) => {
          const match = files.find((f) => f._previewUrl === url);
          if (match) match.imgIndex = index;
        });
        console.log("After handleDragEnd:", files);
        return files;
      });

      setHasDragged(true);
    }
  };

  useEffect(() => {
    if (test && show) {
      setSelectedTestType({
        value: test.test.id,
        label: test.test.name,
      });
      setSelectedSampleType({
        value: test.test_sample.id,
        label: test.test_sample.name,
      });
      setMarkAsComplete(test.status === "finished");
      setTestImagesPreview(
        test.images ? test.images.map((img) => img.url) : []
      );
      setSampleInfoLabel(test.sample_info || []);
      setSampleInfo(
        (test.sample_info || []).map((info) => ({
          label: info.id,
          value:
            info.pivot && info.pivot.value !== undefined
              ? info.pivot.value
              : info.value || "",
        }))
      );
    }
  }, [test, show]);

  const close = () => {
    setTestImagesPreview([]);
    setTestImages([]);
    setIsUploading(false);
    setDisableSampleSelect(false);
    setSelectedTestType(null);
    setSelectedSampleType(null);
    setDisableSave(false);
    setMarkAsComplete(false);
    setSampleTypesData([]);
    setSampleInfo([]);
    setSampleInfoLabel([]);
    setFileTypeError("");
    setIsDelete([]); // Reset deleted images
    onHide();
  };

  const fileChangedHandler = (event) => {
    setFileTypeError("");
    const file = event.target.files;
    const data1 = [];
    const imgview = [];

    if (file) {
      Array.from(event.target.files).forEach((data) => {
        if (data.type !== "image/jpeg" && data.type !== "image/png") {
          setFileTypeError("File uploaded is not PNG or JPG");
        } else if (data.size > fileSizeLimits.avatarBuInspector) {
          setFileTypeError("File size cannot be larger than 2mb");
        } else {
          const previewUrl = URL.createObjectURL(data);
          data._previewUrl = previewUrl; // 🔹 store preview URL inside file
          data1.push(data);
          imgview.push(previewUrl);
        }
      });

      const availableSlots = 4 - testImagesPreview.length;
      const sp = imgview.slice(0, availableSlots);
      const sp1 = data1.slice(0, availableSlots);

      // Add images to preview array
      const startingIndex = testImagesPreview.length;
      setTestImagesPreview((prevImage) => prevImage.concat(sp));

      // Assign imgIndex to each file
      const filesWithIndex = sp1.map((f, index) => {
        f.imgIndex = startingIndex + index;
        return f;
      });

      setTestImages((prev) => prev.concat(filesWithIndex));
    }

    imageInput.current.value = "";
  };

  const uploadImages = (images) => {
    let promiseArray = images.map((image) => {
      return new Promise((resolve, reject) => {
        uploadToS3(image, folders.test_samples)
          .then((res) => {
            resolve(res.url);
          })
          .catch(() => {
            reject(null);
          });
      });
    });
    return promiseArray;
  };

  const handleSave = () => {
    setIsUploading(true);
    setFileTypeError("");
    // Filter only new uploaded files from testImages (they have imgIndex property)
    const newImages = testImages.filter((file) => file instanceof File);

    if (newImages.length > 0) {
      Promise.all(uploadImages(newImages))
        .then((uploadedUrls) => {
          updateTest([...uploadedUrls], newImages);
        })
        .catch(() => {
          showToast("Failed to upload images");
          setIsUploading(false);
        });
    } else {
      updateTest();
    }
  };

  const handleRemoveImage = (removeIndex) => {
    setFileTypeError("");
    // If the image is an existing image (URL with ID), add its ID to isDelete
    const preview = testImagesPreview[removeIndex];
    if (typeof preview === "string" && test && test.images) {
      const imgObj = test.images.find((img) => img.url === preview);
      if (imgObj && imgObj.id) {
        setIsDelete((prev) => [...prev, imgObj.id]);
      }
    }

    setTestImagesPreview((state) =>
      state.filter((_, index) => index !== removeIndex)
    );

    setTestImages((state) => {
      let next = state;
      if (preview && preview.startsWith("blob:")) {
        next = next.filter((file) => file.imgIndex !== removeIndex);
      }
      // Shift indices down for items after the removed index
      next = next.map((file) => {
        if (file.imgIndex > removeIndex) {
          file.imgIndex = file.imgIndex - 1;
        }
        return file;
      });
      return [...next];
    });
  };

  const updateTest = (allImageUrls = [], newFiles = []) => {
    const sample_info_ids = [];
    const sample_info_values = [];
    sampleInfoLabel.forEach((label) => {
      const matchingInfo = sampleInfo.find((info) => info?.label === label?.id);
      sample_info_ids.push(label.id);
      sample_info_values.push(matchingInfo?.value || "");
    });

    const hasNewImages = testImages.some((f) => f instanceof File);
    // Resolve the final preview URLs in order (replace blob: with uploaded URL by imgIndex)
    let resolvedPreviewUrls = [...testImagesPreview];
    if (
      hasNewImages &&
      Array.isArray(allImageUrls) &&
      Array.isArray(newFiles)
    ) {
      const indexToUrl = {};
      newFiles.forEach((file, i) => {
        indexToUrl[file.imgIndex] = allImageUrls[i];
      });
      resolvedPreviewUrls = testImagesPreview.map((url, idx) =>
        typeof url === "string" && url.startsWith("blob:") && indexToUrl[idx]
          ? indexToUrl[idx]
          : url
      );
    }
    const images_sort = hasNewImages
      ? testImages.map((item) => {
          return item.imgIndex;
        })
      : [];

    const data = {
      test_type_id: selectedTestType.value,
      sample_type_id: selectedSampleType.value,
      sample_info_ids,
      sample_info_values,
      status: markAsComplete ? "finished" : "pending",
      delete_images: isDelete,
      // send the new images for association
      ...(hasNewImages ? { images: allImageUrls } : {}),
      // send sort only for newly uploaded images, aligned with `images` order
      ...(hasNewImages ? { images_sort } : {}),
    };

    if (test.images && resolvedPreviewUrls.length) {
      const remainingImageIds = resolvedPreviewUrls
        .map((imgUrl) => {
          const imgObj = test.images.find((img) => img.url === imgUrl);
          return imgObj && imgObj.id ? imgObj.id : null;
        })
        .filter(Boolean);
      const sequence = remainingImageIds.map((_, idx) => idx + 1);
      if ((hasDragged || hasNewImages) && remainingImageIds.length > 0) {
        try {
          orderTestImages({
            image_id: remainingImageIds,
            order_sequence: sequence,
            inspection_id: insp_id,
          });
        } catch (err) {
          console.log("Order sequence Error", err);
        }
      }
      setHasDragged(false);
    }

    updateObservationTest(test.id, data)
      .then(() => {
        showToast("Test updated successfully", true);
        close();
        setRefreshTestList((state) => !state);
      })
      .catch((err) => {
        showToast(getErrorMsg(err) || "Error updating test");
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  useEffect(() => {
    async function btnValidation() {
      if (selectedSampleType && selectedTestType) {
        setDisableSave(false);
      } else {
        setDisableSave(true);
      }
    }
    btnValidation();
  }, [selectedSampleType, selectedTestType, sampleInfo]);

  useEffect(() => {
    if (selectedTestType) {
      setDisableSampleSelect(false);
    } else {
      setDisableSampleSelect(true);
    }
  }, [selectedTestType]);

  const [forceUpdateKey, forceUpdate] = useReducer((x) => x + 1, 0);

  const normalize = (name) =>
    name
      .trim()
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <Modal
      show={show}
      onHide={close}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="no-border">
        <Modal.Title id="contained-modal-title-vcenter">
          <div className="dialogheader">
            <div className="d-flex">
              <h5 style={{ fontWeight: "bolder" }}>Edit Test</h5>
            </div>
            <img
              className="iconstyle crossicon"
              src={require("../../../../../assets/icons/x_light50@3x.png")}
              alt="icon"
              onClick={close}
            />
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "0px" }}>
        <div className="dialogbody">
          <div className="mt-3">
            <label
              className="d-flex align-items-center text-light20 fw-bolder cursor-pointer"
              htmlFor="1"
            >
              <input
                className="me-3 cursor-pointer"
                type="checkbox"
                id="1"
                checked={markAsComplete}
                onChange={(ev) => setMarkAsComplete(ev.target.checked)}
                style={{
                  width: "20px",
                  height: "20px",
                  accentColor: "#007bff",
                }}
              />
              Mark test as completed
            </label>
          </div>
          <div className="mt-3">
            <span className="text-light20 fw-bolder">Test Type</span>
            <div className="mt-2">
              <SearchDropdown
                apiCall={getObservationTestTypes}
                saveProp={"id"}
                showProp={["name"]}
                placeHolder="Select Test Type"
                onSelect={(ev) => {
                  setSelectedTestType(ev);
                  forceUpdate();
                  setSelectedSampleType(null);
                  setSampleInfo([]); // Clear sample info on test type change
                }}
                setSampleTypesData={setSampleTypesData}
                setSampleInfoLabel={setSampleInfoLabel}
                value={selectedTestType}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
              />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-light20 fw-bolder">Sample Type</span>
            <div className="mt-2">
              <div>
                <SearchDropdown
                  localData={sampleTypesData}
                  saveProp="id"
                  showProp={["name"]}
                  placeHolder="Select Sample Type"
                  onSelect={(ev) => setSelectedSampleType(ev)}
                  disabled={disableSampleSelect}
                  value={selectedSampleType}
                  forceUpdateKey={forceUpdateKey}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  customStyles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>
          </div>
          <div className="mt-3">
            {sampleInfoLabel.length > 0 && (
              <span className="text-light20 fw-bolder">
                Sample Info (Optional): {test.code}
              </span>
            )}
            {[...sampleInfoLabel]
              .sort((a, b) => {
                const aName = normalize(a.name);
                const bName = normalize(b.name);
                if (aName === "Lab Code" && bName === "Sample Location")
                  return -1;
                if (aName === "Sample Location" && bName === "Lab Code")
                  return 1;
                return 0;
              })
              .map((sam, i) => (
                <div key={`sample-info-${i}`} className="mt-2">
                  <CustomInput
                    label={normalize(sam.name)}
                    value={sampleInfo[i]?.value || ""}
                    onChange={(e) =>
                      setSampleInfo((state) => {
                        const newState = [...state];
                        newState[i] = {
                          label: sam.id,
                          value: e.target.value,
                        };
                        return newState;
                      })
                    }
                    inputProps={{ maxLength: "50" }}
                  />
                </div>
              ))}
          </div>
          <div className="mt-3">
            <span className="text-light20 fw-bolder">
              Test Photos (optional)
            </span>
            <div className="mt-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={testImagesPreview}
                  strategy={rectSortingStrategy}
                >
                  <div className="d-flex flex-row overflow-x-auto">
                    {testImagesPreview.length === 4 ? (
                      <CustomTooltip text="Maximum 4 test photos can be uploaded">
                        <span style={{ alignSelf: "center" }}>
                          <button
                            className="btn btn-primary inspection-plus-btn me-2"
                            style={{
                              height: "40px",
                              minWidth: "40px",
                              background: "#DEE2DF",
                              border: "#DEE2DF",
                              padding: "5px",
                              cursor:
                                testImagesPreview.length === 4
                                  ? "not-allowed !important"
                                  : "",
                              pointerEvents: "auto",
                            }}
                            disabled={testImagesPreview.length === 4}
                            onClick={() => {
                              imageInput?.current?.click();
                            }}
                            id="plus_inspection_onsite"
                            htmlFor="image_input"
                          >
                            <AddIcon />
                          </button>
                        </span>
                      </CustomTooltip>
                    ) : (
                      <span style={{ alignSelf: "center" }}>
                        <button
                          className="btn btn-primary inspection-plus-btn me-2"
                          style={{
                            height: "40px",
                            minWidth: "40px",
                            background: "#DEE2DF",
                            border: "#DEE2DF",
                            padding: "5px",
                            cursor:
                              testImagesPreview.length === 4
                                ? "not-allowed"
                                : "",
                          }}
                          disabled={testImagesPreview.length === 4}
                          onClick={() => {
                            imageInput?.current?.click();
                          }}
                          id="plus_inspection_onsite"
                          htmlFor="image_input"
                        >
                          <AddIcon />
                        </button>
                      </span>
                    )}
                    {testImagesPreview.map((item, i) => (
                      <SortableImage
                        key={item}
                        id={item}
                        item={item}
                        index={i}
                        onRemove={handleRemoveImage}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {isUploading && testImages.length > 0 && (
                <span className="text-light50 fw-bolder">
                  {uploadingImageText}
                </span>
              )}
              <span
                className="text-error fw-bolder"
                style={{ fontSize: "14px" }}
              >
                {fileTypeError}
              </span>
            </div>
          </div>
        </div>
        <input
          ref={imageInput}
          id="image_input"
          type="file"
          style={{ display: "none" }}
          accept="image/png,image/jpeg"
          multiple={true}
          onChange={fileChangedHandler}
        />
      </Modal.Body>
      <Modal.Footer className="no-border">
        <Button
          id="cancel"
          className="text-secondary500 bg-transparent border-0"
          onClick={close}
        >
          Cancel
        </Button>
        <Button
          id="save_test"
          onClick={handleSave}
          disabled={disableSave || isUploading}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function SortableImage({ id, item, index, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    userSelect: "none",
    cursor: "grab",
    borderRadius: 8,
    position: "relative",
    margin: 8,
    background: "#fff",
    boxShadow: isDragging
      ? "0 8px 32px 0 rgba(0,0,0,0.18)"
      : "0 0 15px 1px rgba(132,133,122,0.5)",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {/* Drag handle button removed */}
      <CancelRoundedIcon
        style={{
          cursor: "pointer",
          position: "absolute",
          top: "6px",
          right: "6px",
          zIndex: 2,
        }}
        onClick={() => onRemove(index)}
      />
      <ImageBox
        src={item}
        style={{
          border: "none",
          height: "110px",
          width: "120px",
          paddingLeft: "8px",
          boxShadow: "none",
        }}
        imgStyle={{
          marginTop: "15px",
          height: "85px",
          width: "112px",
        }}
        link={false}
      />
    </div>
  );
}
